/**
 * Quanto tempo uma alteração publicada leva para chegar no site do cliente.
 *
 * Mora no engine porque servidor e painel precisam da **mesma** resposta: o servidor emite o header, o
 * painel promete o prazo na tela de instalação. Dois números soltos divergiriam, e o sintoma seria o
 * pior tipo — o painel dizendo "5 segundos" com o servidor mandando 60.
 *
 * ## As três camadas
 *
 * Existem três caches entre o clique em Publicar e o visitante ver a mudança, e é fácil contar só dois:
 *
 * | # | Camada | Governada por |
 * |---|---|---|
 * | 1 | Navegador do visitante | `CONFIG_CACHE_SECONDS`, via `max-age` |
 * | 2 | CDN do Firebase Hosting | o mesmo header, compartilhado entre visitantes |
 * | 3 | Memo em processo da Function | `CONFIG_STORE_TTL_MS` |
 *
 * As camadas 2 e 3 são **em série**: o CDN pode gravar uma resposta que a Function montou com dados já
 * velhos. Por isso o pior caso é a soma, e não o maior dos dois — era o que fazia a propagação chegar a
 * dois minutos quando as três estavam em 60s.
 *
 * ## Por que o memo é menor que o do CDN
 *
 * Para ele sair da conta do pior caso. Com os dois em 5s, o pior caso é 10s; com o memo em 2s, é 7s.
 *
 * E custa quase nada: quantas leituras o memo poupa depende de quantas requisições passam do CDN, e
 * nessa faixa de tráfego (algo como 0,4 req/s por chave) ele já quase nunca acerta. Ele continua
 * valendo para o que importa de verdade, que é colapsar uma rajada — um pico de dezenas de req/s numa
 * leitura só.
 *
 * ## O que este número custa
 *
 * O cache do CDN é o que protege a Function, e cada requisição que passa dele custa uma **transação**
 * do Firestore no rate limiter (o filtro em memória só poupa quem já estourou o limite). A 60s o CDN
 * absorvia ~87% do tráfego; a 5s ele para de ajudar quase por completo.
 *
 * Medido para 50 mil conversas/mês, supondo 5% de abertura (~1M buscas de config, porque a config é
 * buscada em todo carregamento de página, não por conversa):
 *
 * | TTL | chega na Function | total da infra/mês |
 * |---|---|---|
 * | 60s | ~130 mil | ~US$ 4,95 |
 * | 30s | ~260 mil | ~US$ 5,70 |
 * | 5s  | ~1 milhão | ~US$ 9,30 |
 *
 * A maior linha da conta não é esta: é a transferência do `widget.js`, que não muda com o TTL. Baixar
 * para 5s move ~US$ 4 sobre uma base de cinco dólares — foi a decisão tomada com esses números à vista.
 *
 * O que **não** protege esse gasto: o teto de conversas. Ele limita conversas, não buscas de config, e
 * um pico de page views num domínio autorizado passa por ele. O freio ali é o rate limit por IP.
 */
/**
 * `max-age` da resposta do `getConfig` — vale para o navegador e para o CDN do Hosting de uma vez, já
 * que não emitimos `s-maxage` separado.
 */
export const CONFIG_CACHE_SECONDS = 5;
/**
 * TTL do memo por instância da Function para o documento do webchat.
 *
 * **Menor que `CONFIG_CACHE_SECONDS` de propósito** — ver o cabeçalho. Há teste fixando essa relação,
 * porque subir este número para "poupar leitura" reintroduz o atraso sem nada quebrar.
 */
export const CONFIG_STORE_TTL_MS = 2000;
/**
 * TTL do memo do contador de conversas do mês.
 *
 * Folgado de propósito, e sem relação com a propagação: publicar mexe no documento do webchat, não
 * neste contador. Ele existe para o teto mensal não acrescentar uma leitura a **toda** invocação do
 * `getConfig`, e errar por alguns segundos na virada de um teto mensal não muda nada.
 */
export const USAGE_STORE_TTL_MS = 60000;
/**
 * Pior caso de propagação, em segundos: as camadas 2 e 3 em série.
 *
 * É o número que o painel promete na tela de instalação. O caso típico é `CONFIG_CACHE_SECONDS`.
 */
export const CONFIG_PROPAGATION_SECONDS = CONFIG_CACHE_SECONDS + CONFIG_STORE_TTL_MS / 1000;
/*
 * Aqui moravam `QUOTA_REFUSAL_CACHE_SECONDS = 60` e a explicação de por que a recusa por cota era a única
 * categoria de erro cacheável.
 *
 * A razão era sólida: teto estourado sem cache não freia gasto nenhum — erro sai com `no-store`, o CDN não
 * absorve, e um webchat acima do limite seguia custando uma requisição por page view, indefinidamente.
 *
 * Sumiu junto com os portões. Os dois tetos mensais deixaram de recusar a config, porque **contagem
 * forjável alimentando bloqueio é negação de serviço direcionada**: `Origin` é forjável fora de um
 * navegador, então quem soubesse a chave pública — que está no HTML da página do cliente, por definição —
 * desligava o widget dele. Medido: ~14 h pelo teto de requisições; ~3 minutos pelo de conversas, que ainda
 * vinha ligado por padrão. Sem recusa por cota, não há prazo a definir.
 *
 * **O raciocínio sobre o que não pode ser cacheado sobreviveu**, vale para sempre, e por isso fica escrito
 * aqui em vez de sair com a constante: cacheável é só recusa que depende **exclusivamente do webchat**,
 * porque a chave de cache é a URL e a URL da config não carrega IP nem Origin. `rate_limited` depende do
 * **IP** — cachear puniria todos os visitantes daquele nó. `domain_not_authorized` depende do **Origin**.
 * `not_published` e `not_found` dependem de estado que acabou de mudar, e foi um 404 cacheado por dez
 * minutos que originou o `no-store` em primeiro lugar. Há teste fixando cada linha dessa lista, porque
 * "unificar isso dentro do `errorResponse`" é uma simplificação plausível que quebraria widget de terceiro.
 *
 * Sequela que a mesma lista explica: como as respostas **cacheáveis** atravessam um CDN que não honra
 * `Vary`, elas mandam `access-control-allow-origin: *` em vez de ecoar o `Origin` — ver `corsAberto` em
 * `apps/backend/src/core/getConfig.ts`.
 */
/**
 * `max-age` da recusa por webchat **suspenso**.
 *
 * Bem maior que o da cota, e por uma diferença de natureza: cota é estado que o dono conserta em
 * minutos, suspensão é estado administrativo que não vira sozinho. Guardar por uma hora deixa um site
 * instalado e abandonado custando centavos por ano — era o cenário do "instalou o SDK e parou de pagar".
 *
 * O preço, e ele precisa estar escrito na tela do painel: depois de reativar, o widget leva até este
 * prazo para voltar em quem já tinha recebido a recusa.
 */
export const SUSPENDED_REFUSAL_CACHE_SECONDS = 3600;
//# sourceMappingURL=cache.js.map